import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { store } from 'store';
import { ShiftDetails, isMultiShiftCustomer } from 'views/Utilization/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ShiftOption {
  startHour: number;
  endHour: number;
  shiftName?: string;
}

/**
 * Single source of truth for shift options.
 * Prefers the backend-driven shift config (customer local time) and only
 * falls back to the hardcoded ShiftDetails map when the API config is absent.
 */
export const resolveShiftOptions = (
  shiftPeriods: ShiftOption[],
  customerCode: string
): ShiftOption[] => {
  if (shiftPeriods.length > 0) return shiftPeriods;
  const code = customerCode?.toLowerCase();
  return ShiftDetails[code] ?? ShiftDetails.others;
};

/** True when the customer runs more than one shift (API config preferred). */
export const resolveIsMultiShift = (
  shiftPeriods: ShiftOption[],
  customerCode: string
): boolean => {
  if (shiftPeriods.length > 0) return shiftPeriods.length > 1;
  return isMultiShiftCustomer(customerCode?.toLowerCase());
};

/**
 * Format a shift boundary hour to HH:mm on the given tz-anchored day.
 * Preserves fractional hours (7.5 -> 07:30) and wraps overnight hours (% 24).
 */
export const formatShiftHour = (hour: number, tzDay: Dayjs): string => {
  const hourOfDay = ((hour % 24) + 24) % 24;
  const whole = Math.floor(hourOfDay);
  const minute = Math.round((hourOfDay - whole) * 60);
  return tzDay.hour(whole).minute(minute).format('HH:mm');
};

/** Non-hook resolver for use outside React (e.g. Excel export). */
export const getShiftConfigFromStore = (
  customerCode: string
): { shiftOptions: ShiftOption[]; isMultiShift: boolean } => {
  const state = store.getState();
  const shiftPeriods =
    state.customerShiftConfigReducer.data?.shiftPeriods ?? [];
  return {
    shiftOptions: resolveShiftOptions(shiftPeriods, customerCode),
    isMultiShift: resolveIsMultiShift(shiftPeriods, customerCode),
  };
};

export const getCustomerShiftsUTCForDate = (
  customerCode: string,
  date: string
) => {
  void date;
  const code = customerCode?.toLowerCase();

  const state = store.getState();
  const { custCode, data } = state.customerShiftConfigReducer;
  const fetchedCode = custCode?.toLowerCase();

  // Backend shiftPeriods are already in customer local time. Keep them as-is.
  const shiftPeriods =
    data?.shiftPeriods && fetchedCode === code ? data.shiftPeriods : [];

  return shiftPeriods.map((period) => {
    const normalizedEndHour =
      period.endHour <= period.startHour ? period.endHour + 24 : period.endHour;
    return {
      startHour: period.startHour,
      endHour: normalizedEndHour,
      shiftName: period.shiftName,
    };
  });
};

/** 0-based index from customer shift config: first period → day, second → night. */
export const getCustomerShiftDisplayName = (zeroBasedIndex: number): string => {
  if (zeroBasedIndex === 0) return 'Day shift';
  if (zeroBasedIndex === 1) return 'Night shift';
  return `Shift ${zeroBasedIndex + 1}`;
};

export const formatCustomerShiftLabel = (
  zeroBasedIndex: number,
  startLocal: string,
  endLocal: string
): string =>
  `${getCustomerShiftDisplayName(zeroBasedIndex)} ${startLocal} - ${endLocal}`;

/**
 * Index of the shift whose [startHour, endHour) window contains `tz`'s
 * current time-of-day, or null if none matches. Boundaries may cross
 * midnight (endHour <= startHour). Unlike getCurrentCustomerShiftInfo, this
 * takes the boundaries directly instead of reading them from Redux, so it
 * resolves immediately from resolveShiftOptions's hardcoded per-customer
 * fallback even when the async shift-config fetch hasn't completed yet.
 */
export const resolveCurrentShiftIndex = (
  options: ShiftOption[],
  tz: string
): number | null => {
  if (options.length === 0) return null;
  const nowInTz = dayjs().tz(tz);
  const minutesSinceStartOfDay = nowInTz.diff(nowInTz.startOf('day'), 'minute');

  for (let index = 0; index < options.length; index += 1) {
    const { startHour, endHour } = options[index];
    const startMinutes = startHour * 60;
    const normalizedEndHour = endHour <= startHour ? endHour + 24 : endHour;
    const endMinutes = normalizedEndHour * 60;
    const minutesToCheck =
      minutesSinceStartOfDay < startMinutes
        ? minutesSinceStartOfDay + 24 * 60
        : minutesSinceStartOfDay;

    if (minutesToCheck >= startMinutes && minutesToCheck < endMinutes) {
      return index;
    }
  }
  return null;
};

export const getCurrentCustomerShiftInfo = (
  customerCode: string,
  customerTz: string
): { currentShiftNumber?: number; currentShiftLabel?: string } => {
  const code = customerCode?.toLowerCase() ?? '';
  const todayInTz = dayjs().tz(customerTz);
  const todayDateStr = todayInTz.format('YYYY-MM-DD');
  const shifts = getCustomerShiftsUTCForDate(code, todayDateStr);

  if (!Array.isArray(shifts) || shifts.length === 0) {
    return { currentShiftNumber: undefined, currentShiftLabel: undefined };
  }

  const nowInCustomerTz = dayjs().tz(customerTz);
  const customerDayStart = dayjs.tz(`${todayDateStr}T00:00:00`, customerTz);
  const minutesSinceStartOfCustomerDay = nowInCustomerTz.diff(
    customerDayStart,
    'minute'
  );

  for (let index = 0; index < shifts.length; index += 1) {
    const { startHour, endHour, shiftName } = shifts[index];
    const startMinutes = startHour * 60;
    const normalizedEndHour = endHour <= startHour ? endHour + 24 : endHour;
    const endMinutes = normalizedEndHour * 60;
    const minutesToCheck =
      minutesSinceStartOfCustomerDay < startMinutes
        ? minutesSinceStartOfCustomerDay + 24 * 60
        : minutesSinceStartOfCustomerDay;

    if (minutesToCheck >= startMinutes && minutesToCheck < endMinutes) {
      const startLocal = customerDayStart
        .add(startHour, 'hour')
        .format('HH:mm');
      const endLocal = customerDayStart
        .add(normalizedEndHour, 'hour')
        .format('HH:mm');

      return {
        currentShiftNumber: index + 1,
        currentShiftLabel: shiftName
          ? `${shiftName} ${startLocal} - ${endLocal}`
          : formatCustomerShiftLabel(index, startLocal, endLocal),
      };
    }
  }

  return { currentShiftNumber: undefined, currentShiftLabel: undefined };
};
